import { useState } from "react";
import { Plus, Pin, PinOff, Pencil, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { AnnouncementEmail } from "./AnnouncementEmail";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { SingleImageUpload } from "@/components/ImageUpload";
import { fmtDate, toLocalInput, fromLocalInput, img } from "@/lib/community";
import { useAdminData, adminAct, Field, NSelect, inputCls, Badge, Panel, btnDark, btnLine, btnDanger } from "./shared";

const CATS = ["This Week at Jenova", "Founder Notes", "Community Announcements", "Upcoming Opportunities", "Weekly Inspiration", "New Releases"];
const AUD = [{ value: "public", label: "Everyone (public)" }, { value: "members", label: "Jenova Girls only" }, { value: "prive", label: "Privé members only" }];
const EMPTY = { title: "", cover_image: "", description: "", body: "", category: CATS[0], date: "", featured: false, pinned: false,
  event_link: "", audience: "public", status: "draft", publish_at: "", notify: true };

function AnnouncementForm({ initial, onSaved, onClose }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, publish_at: toLocalInput(initial?.publish_at) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    setBusy(true);
    const payload = { ...f, publish_at: fromLocalInput(f.publish_at), date: f.date || null, event_link: f.event_link || null };
    const r = await adminAct(() => (initial?.id ? api.put(`/admin/community/announcements/${initial.id}`, payload) : api.post("/admin/community/announcements", payload)), "Saved");
    setBusy(false);
    if (r) onSaved();
  };
  return (
    <div className="space-y-4" data-testid="announcement-form">
      <Field label="Title"><input className={inputCls} value={f.title} onChange={set("title")} data-testid="ann-title-input" /></Field>
      <Field label="Short description"><textarea className={`${inputCls} min-h-[70px]`} value={f.description} onChange={set("description")} data-testid="ann-description-input" /></Field>
      <Field label="Full story (optional)"><textarea className={`${inputCls} min-h-[120px]`} value={f.body || ""} onChange={set("body")} data-testid="ann-body-input" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Category"><NSelect value={f.category} onChange={set("category")} options={CATS} testId="ann-category-select" /></Field>
        <Field label="Who can see it"><NSelect value={f.audience} onChange={set("audience")} options={AUD} testId="ann-audience-select" /></Field>
        <Field label="Display date"><input type="date" className={inputCls} value={f.date || ""} onChange={set("date")} data-testid="ann-date-input" /></Field>
        <Field label="Event link (optional)"><input className={inputCls} value={f.event_link || ""} onChange={set("event_link")} placeholder="/events/... or https://" data-testid="ann-link-input" /></Field>
        <Field label="Status"><NSelect value={f.status} onChange={set("status")} options={[{ value: "draft", label: "Draft" }, { value: "scheduled", label: "Schedule for later" }, { value: "published", label: "Publish now" }]} testId="ann-status-select" /></Field>
        {f.status === "scheduled" && <Field label="Publish at"><input type="datetime-local" className={inputCls} value={f.publish_at} onChange={set("publish_at")} data-testid="ann-publish-at-input" /></Field>}
      </div>
      <Field label="Cover image"><SingleImageUpload value={f.cover_image} onChange={set("cover_image")} testId="ann-cover" aspectRatio="aspect-[16/10]" /></Field>
      <div className="flex flex-wrap gap-6 text-sm">
        <label className="flex items-center gap-2"><Switch checked={f.featured} onCheckedChange={set("featured")} data-testid="ann-featured-switch" /> Featured</label>
        <label className="flex items-center gap-2"><Switch checked={f.pinned} onCheckedChange={set("pinned")} data-testid="ann-pinned-switch" /> Pinned to top</label>
        <label className="flex items-center gap-2"><Switch checked={f.notify} onCheckedChange={set("notify")} data-testid="ann-notify-switch" /> Email subscribers when published</label>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button className={btnLine} onClick={onClose}>Cancel</button>
        <button className={btnDark} disabled={busy} onClick={save} data-testid="ann-save-btn">{busy ? "Saving…" : "Save announcement"}</button>
      </div>
    </div>
  );
}

export default function BulletinTab() {
  const [items, reload] = useAdminData("/admin/community/announcements");
  const [editing, setEditing] = useState(null);
  const [emailing, setEmailing] = useState(null);
  const pin = (a) => adminAct(() => api.post(`/admin/community/announcements/${a.id}/pin`, { pinned: !a.pinned })).then(reload);
  const del = (a) => window.confirm(`Delete "${a.title}"?`) && adminAct(() => api.delete(`/admin/community/announcements/${a.id}`), "Deleted").then(reload);

  return (
    <Panel title="Weekly Bulletin" testId="admin-bulletin-tab"
      action={<button className={btnDark} onClick={() => setEditing({})} data-testid="ann-new-btn"><Plus className="w-3.5 h-3.5" /> New announcement</button>}>
      <div className="divide-y divide-[#F0E8DD]">
        {(items || []).map((a) => (
          <div key={a.id} className="py-4 flex flex-wrap items-center gap-4" data-testid={`ann-row-${a.id}`}>
            <div className="w-20 h-14 rounded overflow-hidden bg-[#F5EFE6] shrink-0">{a.cover_image && <img src={img(a.cover_image)} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1 min-w-[200px]">
              <p className="font-medium text-sm">{a.pinned && <Pin className="inline w-3 h-3 mr-1" />}{a.title}</p>
              <p className="text-xs text-[#7A6A62] mt-0.5">{a.category} · {a.audience} · {a.status === "scheduled" ? `publishes ${fmtDate(a.publish_at)}` : fmtDate(a.date)}{a.notified_count != null ? ` · emailed ${a.notified_count}` : ""}{a.broadcast_count ? ` · sent to ${a.broadcast_count} more` : ""}</p>
            </div>
            <Badge s={a.status} />
            {a.featured && <Badge s="featured" />}
            <div className="flex gap-1.5">
              <button className={btnLine} onClick={() => pin(a)} data-testid={`ann-pin-${a.id}`}>{a.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}</button>
              <button className={btnLine} onClick={() => setEditing(a)} data-testid={`ann-edit-${a.id}`}><Pencil className="w-3.5 h-3.5" /></button>
              {a.status === "published" && (
                <button className={btnLine} onClick={() => setEmailing(a)} data-testid={`ann-email-${a.id}`}><Mail className="w-3.5 h-3.5" /> Send by email</button>
              )}
              <button className={btnDanger} onClick={() => del(a)} data-testid={`ann-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
        {items && !items.length && <p className="text-sm text-[#7A6A62] py-6">No announcements yet — create your first weekly bulletin.</p>}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-serif text-2xl font-light">{editing?.id ? "Edit announcement" : "New announcement"}</DialogTitle>
          {editing && <AnnouncementForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
        </DialogContent>
      </Dialog>
      {emailing && (
        <AnnouncementEmail ann={emailing} onClose={() => setEmailing(null)}
          onSent={(d) => { toast.success(`Sending to ${d.recipients} people`); setEmailing(null); reload(); }} />
      )}
    </Panel>
  );
}
